import { Pageable, PageResponse } from '@/entities/user/api/user.api';
import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';

export type PartnerCompanyType = 'RUDALINK' | 'EXTERNAL';
export type PartnerCompanyContractStatus =
  | 'ACTIVE'
  | 'NOT_CONTRACTED'
  | 'RENEWAL_DUE'
  | 'TERMINATED';
export type PartnerCompanyContractType = 'DEFAULT' | string;
export type PartnerCompanyDocumentType =
  | 'CONTRACT'
  | 'BUSINESS_LICENSE'
  | 'CSO_REPORT'
  | 'BANKBOOK'
  | string;

export interface PartnerCompanyOption {
  partnerCompanyId: number;
  companyName: string;
  companyType: PartnerCompanyType;
  defaultOption: boolean;
}

export interface PartnerCompanyListSummary {
  total: number;
  active: number;
  renewalDue: number;
  terminated: number;
}

export interface PartnerCompanyListItem {
  partnerCompanyId: number;
  companyName: string;
  companyType: PartnerCompanyType;
  businessRegistrationNumber: string;
  representativeName: string;
  /** 판촉영업 신고번호 */
  csoReportNumber?: string | null;
  contactNumber?: string | null;
  active: boolean;
  contractStatus: PartnerCompanyContractStatus;
  contractNumber?: string | null;
  contractType?: PartnerCompanyContractType | null;
  contractStartDate?: string | null;
  contractEndDate?: string | null;
  /** 소속 사용자 수 */
  userCount?: number | null;
  /** 담당 거래처 수 (영업 제휴 건수) */
  businessRelationshipCount?: number | null;
  createdAt?: string | null;
}

export interface PartnerCompanyListResponse {
  summary: PartnerCompanyListSummary;
  page: PageResponse<PartnerCompanyListItem>;
}

export interface PartnerCompanySearchRequest {
  keyword?: string;
  contractStatus?: PartnerCompanyContractStatus;
  contractEndFrom?: string;
  contractEndTo?: string;
}

export interface PartnerCompanyCreateRequest {
  companyName: string;
  companyType: PartnerCompanyType;
  businessRegistrationNumber: string;
  representativeName: string;
  csoReportNumber?: string;
  contactNumber?: string;
  email?: string;
  address?: string;
  bankName?: string;
  accountNumber?: string;
  accountHolder?: string;
}

export interface PartnerCompanyUpdateRequest {
  companyName?: string | null;
  companyType?: PartnerCompanyType | null;
  representativeName?: string | null;
  csoReportNumber?: string | null;
  contactNumber?: string | null;
  email?: string | null;
  address?: string | null;
  bankName?: string | null;
  accountNumber?: string | null;
  accountHolder?: string | null;
  active?: boolean | null;
}

export interface PartnerCompanyContract {
  contractId: number;
  contractNumber: string;
  contractType: PartnerCompanyContractType;
  startDate: string;
  endDate?: string | null;
  settlementTerms?: string | null;
}

export interface PartnerCompanyDocument {
  documentId: number;
  documentType: PartnerCompanyDocumentType;
  originalFileName: string;
  downloadUrl: string;
  createdAt?: string | null;
}

export interface PartnerCompanyAssignment {
  assignmentId: number;
  relationPharmaCompanyId: number;
  pharmaCompanyName?: string | null;
  drugCode?: string | null;
  drugName?: string | null;
  startDate: string;
  endDate?: string | null;
  settlementCompanyId: number;
  settlementCompanyName: string;
  status?: string | null;
}

export interface PartnerCompanyDetailResponse {
  partnerCompanyId: number;
  companyName: string;
  companyType: PartnerCompanyType;
  businessRegistrationNumber: string;
  representativeName: string;
  csoReportNumber?: string | null;
  contactNumber?: string | null;
  email?: string | null;
  address?: string | null;
  bankName?: string | null;
  accountNumber?: string | null;
  accountHolder?: string | null;
  active: boolean;
  contracts: PartnerCompanyContract[];
  documents: PartnerCompanyDocument[];
  assignments: PartnerCompanyAssignment[];
}

export type PartnerCompanyChangeType =
  | 'COMPANY_CREATED'
  | 'COMPANY_INFO_UPDATED'
  | 'SETTLEMENT_INFO_UPDATED'
  | 'COMPANY_STATUS_UPDATED'
  | 'CONTRACT_CREATED'
  | 'CONTRACT_PERIOD_UPDATED'
  | 'DOCUMENT_ADDED'
  | 'ASSIGNMENT_CREATED'
  | 'ASSIGNMENT_CANCELLED';

export interface PartnerCompanyFieldChange {
  field: string;
  label: string;
  before?: string | null;
  after?: string | null;
}

/** GET /admin/partner-companies/{id}/histories */
export interface PartnerCompanyHistoryResponse {
  partnerCompanyHistoryId: number;
  revision: number;
  changeType: PartnerCompanyChangeType;
  changeTypeLabel: string;
  changes: PartnerCompanyFieldChange[];
  effectiveDate?: string | null;
  changedBy?: number | null;
  changedByName?: string | null;
  changedAt?: string | null;
}

export interface PartnerCompanyContractCreateRequest {
  contractNumber: string;
  contractType: PartnerCompanyContractType;
  startDate: string;
  endDate: string;
  settlementTerms?: string;
}

export interface PartnerCompanyContractUpdateRequest {
  startDate?: string;
  endDate?: string;
  settlementTerms?: string;
}

export interface PartnerCompanyDocumentUploadUrlResponse {
  presignedUrl: string;
  uploadKey: string;
  shortKey: string;
}

export interface PartnerCompanyDocumentCreateRequest {
  documentType: PartnerCompanyDocumentType;
  originalFileName: string;
  s3FileKey: string;
}

export interface PartnerCompanyAssignmentCreateRequest {
  relationPharmaCompanyId: number;
  drugCode?: string | null;
  startDate: string;
  endDate?: string | null;
  settlementCompanyId: number;
}

/**
 * 파트너스테이션 로그인 계정.
 * 비밀번호·인증 정보는 응답에 포함되지 않는다.
 */
export interface PartnerCompanyAccount {
  accountId: number;
  partnerCompanyId: number;
  loginEmail: string;
  name: string;
  active: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface PartnerCompanyAccountCreateRequest {
  /** 로그인 이메일 — 업체 연락 이메일과 별도 */
  loginEmail: string;
  /** 초기 비밀번호 (10~128자) */
  password: string;
  name: string;
}

/**
 * null·미전송 항목은 서버가 기존 값을 유지한다.
 * 비밀번호·이메일·활성 상태를 바꾸면 서버가 인증 버전을 갱신해 기존 로그인이 끊긴다.
 */
export interface PartnerCompanyAccountUpdateRequest {
  loginEmail?: string;
  password?: string;
  name?: string;
  active?: boolean;
}

export const partnerCompanyService = {
  getPartnerCompanies: async (
    searchRequest: PartnerCompanySearchRequest,
    pageable: Pageable,
  ): Promise<PartnerCompanyListResponse> => {
    const params: Record<string, string | number> = {
      page: pageable.page,
      size: pageable.size,
    };

    if (searchRequest.keyword) params.keyword = searchRequest.keyword;
    if (searchRequest.contractStatus) {
      params.contractStatus = searchRequest.contractStatus;
    }
    if (searchRequest.contractEndFrom) {
      params.contractEndFrom = searchRequest.contractEndFrom;
    }
    if (searchRequest.contractEndTo) {
      params.contractEndTo = searchRequest.contractEndTo;
    }

    const response = await apiClient.get(createApiPath.partnerCompanies.list(), {
      params,
    });
    return response.data;
  },

  getPartnerCompanyOptions: async (): Promise<PartnerCompanyOption[]> => {
    const response = await apiClient.get(createApiPath.partnerCompanies.options());
    return response.data;
  },

  getPartnerCompanyDetail: async (
    partnerCompanyId: number,
  ): Promise<PartnerCompanyDetailResponse> => {
    const response = await apiClient.get(
      createApiPath.partnerCompanies.detail(partnerCompanyId),
    );
    return response.data;
  },

  createPartnerCompany: async (
    request: PartnerCompanyCreateRequest,
  ): Promise<number> => {
    const response = await apiClient.post(
      createApiPath.partnerCompanies.create(),
      request,
    );
    return response.data;
  },

  updatePartnerCompany: async (
    partnerCompanyId: number,
    request: PartnerCompanyUpdateRequest,
  ): Promise<void> => {
    await apiClient.patch(
      createApiPath.partnerCompanies.update(partnerCompanyId),
      request,
    );
  },

  createContract: async (
    partnerCompanyId: number,
    request: PartnerCompanyContractCreateRequest,
  ): Promise<number> => {
    const response = await apiClient.post(
      createApiPath.partnerCompanies.contracts(partnerCompanyId),
      request,
    );
    return response.data;
  },

  updateContract: async (
    partnerCompanyId: number,
    contractId: number,
    request: PartnerCompanyContractUpdateRequest,
  ): Promise<void> => {
    await apiClient.patch(
      createApiPath.partnerCompanies.contractDetail(
        partnerCompanyId,
        contractId,
      ),
      request,
    );
  },

  getHistories: async (
    partnerCompanyId: number,
  ): Promise<PartnerCompanyHistoryResponse[]> => {
    const response = await apiClient.get(
      createApiPath.partnerCompanies.histories(partnerCompanyId),
    );
    return response.data;
  },

  getDocumentUploadUrl: async (
    fileName: string,
  ): Promise<PartnerCompanyDocumentUploadUrlResponse> => {
    const response = await apiClient.post(
      createApiPath.partnerCompanies.uploadUrl(),
      null,
      {
        params: { filename: fileName },
      },
    );
    return response.data;
  },

  createDocument: async (
    partnerCompanyId: number,
    request: PartnerCompanyDocumentCreateRequest,
  ): Promise<void> => {
    await apiClient.post(
      createApiPath.partnerCompanies.documents(partnerCompanyId),
      request,
    );
  },

  createAssignment: async (
    parentCompanyId: number,
    request: PartnerCompanyAssignmentCreateRequest,
  ): Promise<void> => {
    await apiClient.post(
      createApiPath.partnerCompanies.assignments(parentCompanyId),
      request,
    );
  },

  cancelAssignment: async (
    partnerCompanyId: number,
    assignmentId: number,
  ): Promise<void> => {
    await apiClient.delete(
      createApiPath.partnerCompanies.assignmentDetail(
        partnerCompanyId,
        assignmentId,
      ),
    );
  },

  /** 비활성 계정 포함, 계정 ID 오름차순 */
  getAccounts: async (
    partnerCompanyId: number,
  ): Promise<PartnerCompanyAccount[]> => {
    const response = await apiClient.get(
      createApiPath.partnerCompanies.accounts(partnerCompanyId),
    );
    return response.data;
  },

  /** 응답은 생성된 accountId */
  createAccount: async (
    partnerCompanyId: number,
    request: PartnerCompanyAccountCreateRequest,
  ): Promise<number> => {
    const response = await apiClient.post(
      createApiPath.partnerCompanies.accounts(partnerCompanyId),
      request,
    );
    return response.data;
  },

  updateAccount: async (
    partnerCompanyId: number,
    accountId: number,
    request: PartnerCompanyAccountUpdateRequest,
  ): Promise<void> => {
    await apiClient.patch(
      createApiPath.partnerCompanies.accountDetail(partnerCompanyId, accountId),
      request,
    );
  },
};
